import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
} from "react-bootstrap";
import {
  fetchCharges,
  bulkUpdateCharges,
  createCharge,
  updateCharge,
  archiverCharge,
  supprimerCharge,
} from "./DataService";

/**
 * Les charges mensuelles, fixes et variables.
 *
 * Fixe : un montant HT par mois, dû même si l'on ne livre rien (leasing,
 * salaires, loyer). Variable : un TAUX sur une assiette — € par livraison, € par
 * km, % du CA HT — dont le montant se calcule sur l'activité du mois (carburant,
 * péages, frais de paiement). Voir ChargesMensuellesService côté API.
 *
 * Alimentent la barre « charges couvertes » du dashboard. Toujours en HT — c'est
 * l'unité de toutes les charges, et la seule qui se compare au chiffre d'affaires
 * hors taxes : les salaires n'ont pas de TVA, le reste une TVA récupérable.
 *
 * **Ne pas confondre avec les réglages « Digest ROI ».** Ceux-là chiffrent ce que
 * coûterait au CLIENT sa propre livraison, pour le mail qu'on lui envoie. Les
 * deux nombres sont voisins et de natures différentes : les fondre casserait ce
 * mail. L'avertissement est répété dans l'en-tête de l'écran pour que personne
 * n'essaie de les unifier.
 */

const LIBELLES_CATEGORIE = {
  leasing: "Leasing",
  salaire: "Salaires",
  loyer: "Loyer",
  abonnement: "Abonnements",
  carburant: "Carburant",
  peage: "Péages",
  entretien: "Entretien",
  frais_paiement: "Frais de paiement",
  autre: "Autre",
};

const UNITE_ASSIETTE = {
  livraison: "€ / livraison",
  km: "€ / km",
  ca_ht: "% du CA HT",
};

const VIDE = {
  libelle: "",
  categorie: "abonnement",
  nature: "fixe",
  montant_ht: "",
  assiette: "km",
  taux: "",
};

const euros = (valeur) =>
  `${Number(valeur ?? 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

function ChargesMensuelles() {
  const [charges, setCharges] = useState([]);
  const [montants, setMontants] = useState({});
  // Libellés en cours de saisie, par id, comme les montants.
  const [libelles, setLibelles] = useState({});
  const [categories, setCategories] = useState([]);
  const [assiettes, setAssiettes] = useState([]);
  const [apercu, setApercu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const [nouvelle, setNouvelle] = useState(VIDE);

  const charger = async () => {
    try {
      const data = await fetchCharges();
      setCharges(data.charges ?? []);
      setCategories(data.categories ?? []);
      setAssiettes(data.assiettes ?? []);
      setApercu(data.apercu ?? null);

      const initiaux = {};
      const libellesInitiaux = {};
      (data.charges ?? []).forEach((c) => {
        // Une seule saisie par ligne : le montant d'une fixe, le taux d'une variable.
        initiaux[c.id] = String(c.nature === "variable" ? c.taux ?? 0 : c.montant_ht);
        libellesInitiaux[c.id] = c.libelle;
      });
      setMontants(initiaux);
      setLibelles(libellesInitiaux);
      setError(null);
    } catch (e) {
      console.error("Erreur chargement charges:", e);
      setError("Impossible de charger les charges.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    charger();
  }, []);

  // Le total se recalcule en direct pendant la saisie : c'est le seul chiffre
  // qui compte pour la rentabilité, et l'attendre après enregistrement ferait
  // saisir à l'aveugle.
  const fixes = charges.filter((c) => c.nature !== "variable");
  const variables = charges.filter((c) => c.nature === "variable");
  const valeurInitiale = (c) =>
    Number(c.nature === "variable" ? c.taux ?? 0 : c.montant_ht);
  const totalSaisi = fixes.reduce(
    (somme, c) => somme + (parseFloat(montants[c.id]) || 0),
    0
  );
  const valeursModifiees = charges.some(
    (c) => Math.abs((parseFloat(montants[c.id]) || 0) - valeurInitiale(c)) > 0.00001
  );
  // Les charges dont le libellé a changé. Seules celles-ci partent au serveur :
  // la route groupée ne touche que les montants, exprès, pour qu'une correction
  // de chiffre ne puisse pas écraser un libellé.
  const libellesModifies = charges.filter(
    (c) => (libelles[c.id] ?? c.libelle).trim() !== c.libelle
  );
  const modifie = valeursModifiees || libellesModifies.length > 0;

  const enregistrer = async (e) => {
    e.preventDefault();

    if (libellesModifies.some((c) => !(libelles[c.id] ?? "").trim())) {
      setError("Un libellé ne peut pas être vide.");
      return;
    }

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      for (const c of libellesModifies) {
        await updateCharge(c.id, {
          libelle: libelles[c.id].trim(),
          categorie: c.categorie,
        });
      }
      await bulkUpdateCharges(
        charges.map((c) =>
          c.nature === "variable"
            ? { id: c.id, taux: parseFloat(montants[c.id]) || 0 }
            : { id: c.id, montant_ht: parseFloat(montants[c.id]) || 0 }
        )
      );
      await charger();
      setMessage("Charges enregistrées.");
    } catch {
      setError("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const ajouter = async (e) => {
    e.preventDefault();

    if (!nouvelle.libelle.trim()) {
      setError("Donnez un libellé à la charge.");
      return;
    }

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      await createCharge(
        nouvelle.nature === "variable"
          ? {
              libelle: nouvelle.libelle.trim(),
              categorie: nouvelle.categorie,
              nature: "variable",
              assiette: nouvelle.assiette,
              taux: parseFloat(nouvelle.taux) || 0,
            }
          : {
              libelle: nouvelle.libelle.trim(),
              categorie: nouvelle.categorie,
              nature: "fixe",
              montant_ht: parseFloat(nouvelle.montant_ht) || 0,
            }
      );
      setNouvelle(VIDE);
      await charger();
      setMessage("Charge ajoutée.");
    } catch {
      setError("Impossible d'ajouter cette charge.");
    } finally {
      setSaving(false);
    }
  };

  const arreter = async (charge) => {
    // « Arrêter » : la charge s'arrête vraiment (leasing résilié, abonnement
    // terminé). Elle reste comptée jusqu'à la fin du mois en cours.
    const ok = window.confirm(
      `Arrêter « ${charge.libelle} » à la fin du mois en cours ?\n\n` +
        `Elle reste comptée jusqu'au dernier jour du mois, puis disparaît des charges. ` +
        `Pour une ligne saisie par erreur, utilisez plutôt « Supprimer ».`
    );

    if (!ok) return;

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const reponse = await archiverCharge(charge.id);
      await charger();
      setMessage(reponse.message ?? "Charge arrêtée.");
    } catch {
      setError("Impossible d'arrêter cette charge.");
    } finally {
      setSaving(false);
    }
  };

  // Pour une ligne saisie par erreur : effacée, elle ne compte plus du tout,
  // même pour le mois en cours.
  const supprimer = async (charge) => {
    const ok = window.confirm(
      `Supprimer définitivement « ${charge.libelle} » ?\n\n` +
        `La ligne est effacée et ne compte plus, même pour le mois en cours. ` +
        `Si la charge s'arrête simplement, utilisez plutôt « Arrêter ».`
    );

    if (!ok) return;

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const reponse = await supprimerCharge(charge.id);
      await charger();
      setMessage(reponse.message ?? "Charge supprimée.");
    } catch {
      setError("Impossible de supprimer cette charge.");
    } finally {
      setSaving(false);
    }
  };

  // Montant du mois en cours, à date, d'une charge variable (aperçu serveur).
  const apercuDe = (id) =>
    (apercu?.variables_detail ?? []).find((v) => v.id === id);

  const tableau = (lignes, nature) => (
    <Table responsive className="align-middle mb-3">
      <thead>
        <tr style={{ fontSize: 13 }}>
          <th>Charge</th>
          <th style={{ width: 150 }}>Catégorie</th>
          <th style={{ width: 200 }}>
            {nature === "variable" ? "Taux" : "Montant HT / mois"}
          </th>
          {nature === "variable" && <th style={{ width: 200 }}>Ce mois-ci, à date</th>}
          <th style={{ width: 120 }}>Depuis</th>
          <th style={{ width: 170 }}></th>
        </tr>
      </thead>
      <tbody>
        {lignes.length === 0 && (
          <tr>
            <td colSpan={nature === "variable" ? 6 : 5} className="text-muted text-center py-3">
              {nature === "variable"
                ? "Aucune charge variable : ajoutez le carburant, les péages ou les frais de paiement ci-dessous."
                : "Aucune charge fixe active."}
            </td>
          </tr>
        )}
        {lignes.map((c) => {
          const a = nature === "variable" ? apercuDe(c.id) : null;
          return (
            <tr key={c.id}>
              <td>
                <Form.Control
                  type="text"
                  size="sm"
                  maxLength={100}
                  value={libelles[c.id] ?? ""}
                  onChange={(e) =>
                    setLibelles((prev) => ({ ...prev, [c.id]: e.target.value }))
                  }
                />
              </td>
              <td>
                <Badge bg="secondary" className="fw-normal">
                  {LIBELLES_CATEGORIE[c.categorie] ?? c.categorie}
                </Badge>
              </td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="number"
                    step={nature === "variable" ? "0.0001" : "0.01"}
                    min="0"
                    size="sm"
                    value={montants[c.id] ?? ""}
                    onChange={(e) =>
                      setMontants((prev) => ({ ...prev, [c.id]: e.target.value }))
                    }
                  />
                  {nature === "variable" && (
                    <span className="text-muted text-nowrap" style={{ fontSize: 12 }}>
                      {UNITE_ASSIETTE[c.assiette] ?? c.assiette}
                    </span>
                  )}
                </div>
              </td>
              {nature === "variable" && (
                <td style={{ fontSize: 13 }}>
                  {a && a.montant_ht !== null ? (
                    <>
                      <strong>{euros(a.montant_ht)}</strong>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        sur {Number(a.base).toLocaleString("fr-FR")}{" "}
                        {c.assiette === "km" ? "km" : c.assiette === "ca_ht" ? "€ de CA" : "livraisons"}
                      </div>
                    </>
                  ) : a ? (
                    <span className="text-muted">N/D (aucun shift)</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              )}
              <td className="text-muted" style={{ fontSize: 13 }}>
                {c.actif_du ? new Date(c.actif_du).toLocaleDateString("fr-FR") : "—"}
              </td>
              <td>
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={saving}
                  onClick={() => arreter(c)}
                >
                  Arrêter
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger ms-1 p-0"
                  disabled={saving}
                  onClick={() => supprimer(c)}
                >
                  Supprimer
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h1 className="h3 mb-1">Charges mensuelles</h1>
      <p className="text-muted" style={{ fontSize: 14 }}>
        Les charges fixes et variables, en <strong>HT</strong>. Elles alimentent
        la barre « charges couvertes » du dashboard, comparée au chiffre d'affaires hors
        taxes — les salaires n'ont pas de TVA et le reste une TVA récupérable,
        donc un total TTC ne voudrait rien dire.
      </p>

      <Alert variant="light" className="border" style={{ fontSize: 13 }}>
        <strong>À ne pas confondre avec « Digest ROI ».</strong> Les réglages de
        cet écran-là (leasing, coût horaire) chiffrent ce que coûterait au{" "}
        <em>client</em> sa propre livraison, pour le mail qu'on lui envoie. Ce
        sont des montants voisins mais de natures différentes : les deux écrans
        doivent rester séparés.
      </Alert>

      {message && (
        <Alert variant="success" className="py-2">
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form onSubmit={enregistrer}>
            <h2 className="h6 mb-2">Charges fixes</h2>
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              Dues même si l'on ne livre rien du mois : un montant HT mensuel.
            </p>
            {tableau(fixes, "fixe")}

            <h2 className="h6 mb-2 mt-4">Charges variables</h2>
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              Elles grossissent avec l'activité : un taux par livraison, par km
              (km des shifts) ou en % du CA HT. Le montant du mois se calcule sur
              l'activité réelle.
            </p>
            {tableau(variables, "variable")}

            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="text-muted" style={{ fontSize: 13 }}>
                  Total des charges fixes
                </span>
                <div style={{ fontSize: 20, fontWeight: 600 }}>
                  {euros(totalSaisi)} HT
                  {modifie && (
                    <span
                      className="text-warning ms-2"
                      style={{ fontSize: 13, fontWeight: 400 }}
                    >
                      non enregistré
                    </span>
                  )}
                </div>
                {apercu && variables.length > 0 && (
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    + variables ce mois-ci, à date :{" "}
                    <strong>{euros(apercu.variables_ht)}</strong>
                    {apercu.cout_variable_commande !== null && (
                      <>
                        {" "}· coût variable d'une commande moyenne :{" "}
                        <strong>{euros(apercu.cout_variable_commande)}</strong>
                      </>
                    )}
                    {" "}(taux enregistrés, sur {apercu.livraisons} livraisons)
                  </div>
                )}
              </div>
              <Button type="submit" disabled={saving || !modifie}>
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <h2 className="h6 mb-3">Ajouter une charge</h2>
          <Form onSubmit={ajouter}>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <Form.Label style={{ fontSize: 13 }}>Nature</Form.Label>
                <Form.Select
                  size="sm"
                  value={nouvelle.nature}
                  onChange={(e) => {
                    const nature = e.target.value;
                    setNouvelle((p) => ({
                      ...p,
                      nature,
                      categorie: nature === "variable" ? "carburant" : "abonnement",
                    }));
                  }}
                >
                  <option value="fixe">Fixe</option>
                  <option value="variable">Variable</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label style={{ fontSize: 13 }}>Libellé</Form.Label>
                <Form.Control
                  size="sm"
                  value={nouvelle.libelle}
                  placeholder={nouvelle.nature === "variable" ? "Gazole" : "Assurance flotte"}
                  onChange={(e) =>
                    setNouvelle((p) => ({ ...p, libelle: e.target.value }))
                  }
                />
              </Col>
              <Col md={2}>
                <Form.Label style={{ fontSize: 13 }}>Catégorie</Form.Label>
                <Form.Select
                  size="sm"
                  value={nouvelle.categorie}
                  onChange={(e) =>
                    setNouvelle((p) => ({ ...p, categorie: e.target.value }))
                  }
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {LIBELLES_CATEGORIE[cat] ?? cat}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {nouvelle.nature === "variable" ? (
                <>
                  <Col md={2}>
                    <Form.Label style={{ fontSize: 13 }}>Assiette</Form.Label>
                    <Form.Select
                      size="sm"
                      value={nouvelle.assiette}
                      onChange={(e) =>
                        setNouvelle((p) => ({ ...p, assiette: e.target.value }))
                      }
                    >
                      {assiettes.map((a) => (
                        <option key={a} value={a}>
                          {UNITE_ASSIETTE[a] ?? a}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={1}>
                    <Form.Label style={{ fontSize: 13 }}>Taux</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.0001"
                      min="0"
                      size="sm"
                      value={nouvelle.taux}
                      onChange={(e) =>
                        setNouvelle((p) => ({ ...p, taux: e.target.value }))
                      }
                    />
                  </Col>
                </>
              ) : (
                <Col md={3}>
                  <Form.Label style={{ fontSize: 13 }}>Montant HT / mois</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    value={nouvelle.montant_ht}
                    onChange={(e) =>
                      setNouvelle((p) => ({ ...p, montant_ht: e.target.value }))
                    }
                  />
                </Col>
              )}
              <Col md={2}>
                <Button
                  type="submit"
                  variant="outline-primary"
                  size="sm"
                  className="w-100"
                  disabled={saving}
                >
                  Ajouter
                </Button>
              </Col>
            </Row>
          </Form>
          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            La charge vaut à partir du 1er du mois en cours : saisie le 18, elle
            compte pour le mois entier. Variable : le taux s'applique à toute
            l'activité du mois (ex. gazole 0,25 € / km, frais Stripe 1,5 % du CA).
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ChargesMensuelles;
